import React, { useState, useEffect } from 'react';
import { Edit2, Plus, Trash2, Save, X, Eye, FileText, Smartphone, Mail, MessageSquare, AlertTriangle } from 'lucide-react';
import CustomSelect from './CustomSelect';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';
import EmisorManager from './EmisorManager';
import EmailPreview from './EmailPreview';


//const API_BASE = 'http://localhost:3001';
const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

const PlantillasManager = ({ registrarActividad, tiposAlerta = [], onGuardarSuccess, usuario }) => {
    const [activeTab, setActiveTab] = useState('plantillas');

    const [plantillas, setPlantillas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [editando, setEditando] = useState(null); // ID de la plantilla en edición o 'nueva'

    const [datosPlantilla, setDatosPlantilla] = useState({
        nombre: '',
        tipo: 'sms',
        tipo_alerta_codigo: 'GEN',
        asunto: '',
        mensaje: '',
        variables: [],
        content_sid: '',
        categoria: 'utility',
        activa: true
    });

    const [nuevaVariable, setNuevaVariable] = useState('');

    useEffect(() => {
        fetchPlantillas();
    }, []);

    const fetchPlantillas = async () => {
        setCargando(true);
        try {
            const res = await fetch(`${API_BASE}/api/plantillas`, {
                method: 'GET',
                headers: getAuthorizedHeaders(),
            });
            if (res.ok) {
                const data = await res.json();
                setPlantillas(data);
            } else {
                setError('Error al cargar las plantillas');
            }
        } catch (err) {
            setError('Error de conexión con el servidor');
        } finally {
            setCargando(false);
        }
    };

    const handleIniciarNueva = () => {
        setDatosPlantilla({
            nombre: '',
            tipo: 'sms',
            tipo_alerta_codigo: 'GEN',
            asunto: '',
            mensaje: '',
            variables: [],
            content_sid: '',
            categoria: 'utility',
            activa: true
        });
        setEditando('nueva');
    };

    const handleIniciarEditar = (plantilla) => {
        setDatosPlantilla({
            ...plantilla,
            variables: Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]'),
            activa: !!plantilla.activa
        });
        setEditando(plantilla.id);
    };

    const handleCancelar = () => {
        setEditando(null);
        setError(null);
    };

    const handleAgregarVariable = () => {
        if (nuevaVariable && !datosPlantilla.variables.includes(nuevaVariable)) {
            setDatosPlantilla({
                ...datosPlantilla,
                variables: [...datosPlantilla.variables, nuevaVariable]
            });
            setNuevaVariable('');
        }
    };

    const handleEliminarVariable = (index) => {
        const nuevasVars = [...datosPlantilla.variables];
        nuevasVars.splice(index, 1);
        setDatosPlantilla({ ...datosPlantilla, variables: nuevasVars });
    };

    // Renderizador de formato nativo de WhatsApp (*negrita*, _cursiva_, y enlaces como malagaresponde.es)
    const renderizarFormatoWhatsApp = (texto) => {
        if (!texto) return 'Empiece a escribir para ver la vista previa...';
        const lineas = String(texto).split('\n');
        const tokenRegex = /(\*[^*\n]+\*|_[^_\n]+_|https?:\/\/[^\s]+|malagaresponde\.es)/g;
        return lineas.map((linea, idxLinea) => {
            const partes = linea.split(tokenRegex);
            return (
                <React.Fragment key={idxLinea}>
                    {partes.map((p, i) => {
                        if (!p) return null;
                        if (p.startsWith('*') && p.endsWith('*') && p.length > 2) {
                            return <strong key={i} className="font-bold text-white">{p.slice(1, -1)}</strong>;
                        }
                        if (p.startsWith('_') && p.endsWith('_') && p.length > 2) {
                            return <em key={i} className="italic text-gray-200">{p.slice(1, -1)}</em>;
                        }
                        if (p === 'malagaresponde.es' || /^https?:\/\//.test(p)) {
                            const href = p.startsWith('http') ? p : `https://${p}`;
                            return (
                                <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-400 underline hover:text-emerald-300">
                                    {p}
                                </a>
                            );
                        }
                        return <span key={i}>{p}</span>;
                    })}
                    {idxLinea < lineas.length - 1 && <br />}
                </React.Fragment>
            );
        });
    };

    const handleGuardar = async () => {
        if (!datosPlantilla.nombre || !datosPlantilla.mensaje) {
            setError('El nombre y el mensaje son obligatorios');
            return;
        }

        const url = editando === 'nueva' 
            ? `${API_BASE}/api/plantillas` 
            : `${API_BASE}/api/plantillas/${editando}`;
        
        const method = editando === 'nueva' ? 'POST' : 'PUT';

        try {
            const res = await fetch(url, {
                method,
                headers: getAuthorizedHeaders(),
                body: JSON.stringify(datosPlantilla)
            });

            if (res.ok) {
                registrarActividad(
                    `Plantilla ${datosPlantilla.nombre} ${editando === 'nueva' ? 'creada' : 'actualizada'}`,
                    'plantillas',
                    'info'
                );
                handleCancelar();
                fetchPlantillas();
                if (onGuardarSuccess) onGuardarSuccess();
            } else {
                const data = await res.json();
                setError(data.error || 'Error al guardar la plantilla');
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const handleEliminar = async (id, nombre) => {
        if (!window.confirm(`¿Estás seguro de que deseas eliminar la plantilla "${nombre}"?`)) return;

        try {
            const res = await fetch(`${API_BASE}/api/plantillas/${id}`, {
                method: 'DELETE',
                headers: getAuthorizedHeaders(),
            });

            if (res.ok) {
                registrarActividad(`Plantilla ${nombre} eliminada`, 'plantillas', 'warning');
                fetchPlantillas();
            } else {
                const data = await res.json();
                alert(data.error || 'No se pudo eliminar la plantilla');
            }
        } catch (err) {
            alert('Error de conexión');
        }
    };

    const getIconoTipo = (tipo) => {
        switch (tipo) {
            case 'email': return <Mail size={16} className="text-blue-500" />;
            case 'sms': return <Smartphone size={16} className="text-green-500" />;
            case 'whatsapp': return <MessageSquare size={16} className="text-green-600" />;
            default: return <FileText size={16} />;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">Plantillas</h2>
                {activeTab === 'plantillas' && !editando && (
                    <button
                        onClick={handleIniciarNueva}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                    >
                        <Plus size={20} />
                        Nueva Plantilla
                    </button>
                )}
            </div>

            {/* Pestanas */}
            <div className="flex border-b border-gray-200 gap-1">
                {['plantillas', 'emisores'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 text-sm font-medium capitalize rounded-t-lg transition ${activeTab === tab ? 'bg-white border border-b-white text-primary-600 -mb-px' : 'text-gray-500 hover:text-gray-700'}`}>
                        {tab === 'plantillas' ? 'Plantillas' : 'Emisores'}
                    </button>
                ))}
            </div>

            {activeTab === 'emisores' && <EmisorManager usuario={usuario} />}

            {activeTab === 'plantillas' && editando && (

                <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-primary-500 animate-in fade-in duration-300">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-semibold text-gray-800">
                            {editando === 'nueva' ? 'Crear Nueva Plantilla' : `Editando: ${datosPlantilla.nombre}`}
                        </h3>
                        <button onClick={handleCancelar} className="text-gray-400 hover:text-gray-600">
                            <X size={20} />
                        </button>
                    </div>

                    {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-sm">{error}</div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Identificativo *</label>
                                <input
                                    type="text"
                                    value={datosPlantilla.nombre}
                                    onChange={(e) => setDatosPlantilla({ ...datosPlantilla, nombre: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                    placeholder="Ej: Alerta Clima Extremo"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Canal de Envío</label>
                                    <CustomSelect
                                        value={datosPlantilla.tipo}
                                        onChange={(v) => setDatosPlantilla({ ...datosPlantilla, tipo: v })}
                                        options={[
                                            { value: 'sms', label: 'SMS' },
                                            { value: 'email', label: 'Email' },
                                            { value: 'whatsapp', label: 'WhatsApp' },
                                        ]}
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                                        <div className="mt-2">
                                            <label className="inline-flex items-center cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    checked={datosPlantilla.activa}
                                                    onChange={(e) => setDatosPlantilla({ ...datosPlantilla, activa: e.target.checked })}
                                                    className="sr-only peer" 
                                                />
                                                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                                <span className="ms-3 text-sm font-medium text-gray-700 font-bold">{datosPlantilla.activa ? 'Activa' : 'Inactiva'}</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Vincular a Alerta *</label>
                                        <input
                                            type="text"
                                            value={datosPlantilla.tipo_alerta_codigo || ''}
                                            onChange={(e) => setDatosPlantilla({ ...datosPlantilla, tipo_alerta_codigo: e.target.value.toUpperCase() })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none font-bold text-primary-700"
                                            placeholder="GEN, M20..."
                                        />
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {tiposAlerta.map(t => (
                                                <button 
                                                    key={t.codigo}
                                                    type="button"
                                                    onClick={() => setDatosPlantilla({...datosPlantilla, tipo_alerta_codigo: t.codigo})}
                                                    className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${datosPlantilla.tipo_alerta_codigo === t.codigo ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    {t.codigo}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {datosPlantilla.tipo === 'email' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Asunto del Email</label>
                                    <input
                                        type="text"
                                        value={datosPlantilla.asunto}
                                        onChange={(e) => setDatosPlantilla({ ...datosPlantilla, asunto: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="Asunto que verá el usuario"
                                    />
                                </div>
                            )}

                            {datosPlantilla.tipo === 'whatsapp' && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Twilio Content SID</label>
                                        <input
                                            type="text"
                                            value={datosPlantilla.content_sid}
                                            onChange={(e) => setDatosPlantilla({ ...datosPlantilla, content_sid: e.target.value })}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                            placeholder="HX..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                                        <CustomSelect
                                            value={datosPlantilla.categoria}
                                            onChange={(v) => setDatosPlantilla({ ...datosPlantilla, categoria: v })}
                                            options={[
                                                { value: 'utility', label: 'Utilidad' },
                                                { value: 'marketing', label: 'Marketing' },
                                                { value: 'authentication', label: 'Autenticación' },
                                            ]}
                                        />
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="block text-sm font-medium text-gray-700">
                                        {datosPlantilla.tipo === 'sms' ? 'Texto del SMS (Texto plano, sin HTML) *' : 'Cuerpo del Mensaje *'}
                                    </label>
                                    {datosPlantilla.tipo === 'sms' && (
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                            (datosPlantilla.mensaje || '').length > 160 
                                                ? 'bg-amber-100 text-amber-800' 
                                                : 'bg-gray-100 text-gray-600'
                                        }`}>
                                            {(datosPlantilla.mensaje || '').length} / 160 caracteres ({Math.ceil(((datosPlantilla.mensaje || '').length || 1) / 160)} {Math.ceil(((datosPlantilla.mensaje || '').length || 1) / 160) > 1 ? 'segmentos' : 'segmento'})
                                        </span>
                                    )}
                                </div>
                                <textarea
                                    value={datosPlantilla.mensaje}
                                    onChange={(e) => setDatosPlantilla({ ...datosPlantilla, mensaje: e.target.value })}
                                    rows={5}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none resize-none font-sans text-sm shadow-inner"
                                    placeholder={
                                        datosPlantilla.tipo === 'sms'
                                            ? "Escribe el texto directo del SMS (sin HTML). Ej: Hola {{nombre}}, te avisamos de..."
                                            : datosPlantilla.tipo === 'whatsapp'
                                            ? "Para WhatsApp use variables numéricas como {{1}}, {{2}} correspondientes a la plantilla aprobada."
                                            : "Escriba su mensaje aquí..."
                                    }
                                ></textarea>

                                {datosPlantilla.tipo === 'sms' && /<[^>]+>/i.test(datosPlantilla.mensaje || '') && (
                                    <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between text-xs text-amber-800">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle size={16} className="text-amber-600 shrink-0" />
                                            <span>Los SMS no admiten HTML. Las etiquetas se enviarían tal cual al móvil del usuario.</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setDatosPlantilla({
                                                ...datosPlantilla,
                                                mensaje: datosPlantilla.mensaje.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
                                            })}
                                            className="px-2.5 py-1 bg-amber-200 hover:bg-amber-300 text-amber-900 rounded font-semibold whitespace-nowrap text-xs transition"
                                        >
                                            Eliminar etiquetas HTML
                                        </button>
                                    </div>
                                )}

                                <p className="text-[11px] text-gray-400 mt-1 italic">
                                    {datosPlantilla.tipo === 'sms'
                                        ? "En SMS escribe texto directo sin etiquetas <html> ni <p>. Si supera 160 caracteres, el operador enviará múltiples segmentos concatenados."
                                        : datosPlantilla.tipo === 'whatsapp'
                                        ? "Nota: Si la plantilla es de WhatsApp y usa Content SID, el mensaje debe coincidir exactamente con el aprobado en Twilio."
                                        : ""}
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Variables Dinámicas</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={nuevaVariable}
                                        onChange={(e) => setNuevaVariable(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAgregarVariable()}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="Nombre de variable (ej: nombre)"
                                    />
                                    <button 
                                        type="button"
                                        onClick={handleAgregarVariable}
                                        className="bg-gray-100 p-2 rounded-lg hover:bg-gray-200 text-gray-600"
                                    >
                                        <Plus size={24} />
                                    </button>
                                </div>

                                {/* Sugerencias automáticas */}
                                {(() => {
                                    const textoCompleto = (datosPlantilla.mensaje || '') + (datosPlantilla.asunto || '');
                                    const detectadas = [...textoCompleto.matchAll(/{{(.*?)}}/g)].map(m => m[1]);
                                    const noRegistradas = detectadas.filter(v => !datosPlantilla.variables?.includes(v));
                                    const unicas = [...new Set(noRegistradas)];

                                    if (unicas.length > 0) {
                                        return (
                                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-100 rounded text-[10px] text-yellow-800">
                                                <p className="font-bold mb-1 italic">Variables detectadas en el texto:</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {unicas.map(v => (
                                                        <button 
                                                            key={v}
                                                            type="button"
                                                            onClick={() => setDatosPlantilla({ ...datosPlantilla, variables: [...(datosPlantilla.variables || []), v] })}
                                                            className="bg-white px-1.5 py-0.5 rounded border border-yellow-200 hover:bg-yellow-100 transition-colors flex items-center gap-1"
                                                        >
                                                            <Plus size={10} /> {v}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {datosPlantilla.variables && datosPlantilla.variables.map((v, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 px-2 py-1 rounded text-xs border border-primary-100">
                                            {v}
                                            {datosPlantilla.tipo === 'whatsapp' && <span className="text-[10px] opacity-70">(&#123;&#123;{i+1}&#125;&#125;)</span>}
                                            <button onClick={() => handleEliminarVariable(i)} className="hover:text-red-500 ml-1">
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                    {(!datosPlantilla.variables || datosPlantilla.variables.length === 0) && (
                                        <p className="text-xs text-gray-400 italic">No se han definido variables.</p>
                                    )}
                                </div>
                            </div>

                            <div className={`bg-gray-50 p-4 rounded-lg ${datosPlantilla.tipo === 'email' ? 'min-h-[400px]' : 'min-h-[320px]'} flex flex-col`}>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 flex items-center gap-1">
                                    <Eye size={12} /> Vista Previa
                                </label>
                                {datosPlantilla.tipo === 'email' ? (
                                    <EmailPreview
                                        mensaje={datosPlantilla.mensaje}
                                        asunto={datosPlantilla.asunto}
                                        altura="h-[340px]"
                                        mostrarToggle={true}
                                    />
                                ) : datosPlantilla.tipo === 'whatsapp' ? (
                                    <div className="flex-1 bg-[#111b21] rounded-xl p-4 overflow-y-auto shadow-inner border border-gray-800">
                                        <div className="bg-[#202c33] text-gray-100 rounded-lg p-3.5 text-sm leading-relaxed shadow">
                                            {renderizarFormatoWhatsApp(datosPlantilla.mensaje)}
                                        </div>
                                        {datosPlantilla.content_sid && (
                                            <div className="mt-3 pt-2 border-t border-gray-700 flex items-center justify-between text-[11px] font-mono text-emerald-400">
                                                <span>Twilio Content SID: {datosPlantilla.content_sid}</span>
                                                <span className="bg-emerald-900/50 text-emerald-300 px-2 py-0.5 rounded">Fuera de ventana 24h</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex-1 bg-white border border-dashed border-gray-300 rounded p-4 overflow-y-auto">
                                        <div className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                                            {datosPlantilla.mensaje || 'Empiece a escribir para ver la vista previa...'}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end pt-4">
                                <button
                                    onClick={handleCancelar}
                                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleGuardar}
                                    className="flex items-center justify-center gap-2 bg-primary-600 text-white px-8 py-2.5 rounded-lg hover:bg-primary-700 transition-colors shadow-md"
                                >
                                    <Save size={20} />
                                    {editando === 'nueva' ? 'Crear Plantilla' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'plantillas' && <div className="bg-white rounded-lg shadow-md overflow-hidden border">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[600px] text-left text-sm text-gray-700">
                    <thead className="bg-gray-50 text-gray-900 font-bold border-b">
                        <tr>
                            <th className="px-6 py-4">Plantilla / Categoría</th>
                            <th className="px-6 py-4">Canal</th>
                            <th className="px-6 py-4">Estado</th>
                            <th className="px-6 py-4">Última Modificación</th>
                            <th className="px-6 py-4 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {cargando ? (
                            <tr><td colSpan="5" className="px-6 py-10 text-center text-gray-400">Cargando plantillas...</td></tr>
                        ) : plantillas.length === 0 ? (
                            <tr><td colSpan="5" className="px-6 py-10 text-center text-gray-400">No hay plantillas registradas.</td></tr>
                        ) : plantillas.map(p => (
                            <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="font-semibold text-gray-900">{p.nombre}</div>
                                    <div className="text-xs text-gray-500 capitalize">{p.categoria || 'Genérica'}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        {getIconoTipo(p.tipo)}
                                        <span className="capitalize">{p.tipo}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${p.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                        {p.activa ? 'Activa' : 'Inactiva'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-xs text-gray-500">
                                    {new Date(p.fecha_modificacion).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2 text-gray-400">
                                        <button 
                                            onClick={() => handleIniciarEditar(p)}
                                            className="p-1.5 hover:bg-primary-50 hover:text-primary-600 rounded transition-colors" 
                                            title="Editar"
                                        >
                                            <Edit2 size={18} />
                                        </button>
                                        <button 
                                            onClick={() => handleEliminar(p.id, p.nombre)}
                                            className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded transition-colors"
                                            title="Eliminar"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>}
        </div>
    );

};

export default PlantillasManager;
